import {
  cancelRedemptionSchema,
  type ChallengeDto,
  type ChallengeKind,
  type ChallengeMetric,
  type CreateChallengeInput,
  type CreatePrizeItemInput,
  type PrizeItemDto,
  type PrizeRedemptionDto,
  type PrizeRedemptionStatus,
  type UpdateChallengeInput,
  type UpdatePrizeItemInput,
} from '@gc/shared';

import { api } from '@/lib/api';

/**
 * Pure mapping and endpoint helpers for the Portal Content screens.
 *
 * Kept apart from the components so the load-bearing conversions — blank stock
 * meaning UNLIMITED (null) rather than zero, datetime-local values crossing the
 * wire as UTC ISO, a cancellation never reaching the API without a reason —
 * are unit-testable without a DOM.
 */

export const CHALLENGES_PATH = '/engagement/challenges';
/** The drawer's Game picker, under `portal.manage` (not `/settings/games`). */
export const CHALLENGE_GAMES_PATH = '/engagement/challenge-games';
export const PRIZE_CATEGORIES_PATH = '/engagement/prize-categories';
export const PRIZES_PATH = '/engagement/prizes';
export const REDEMPTIONS_PATH = '/engagement/redemptions';

/* -------------------------------------------------------------------------- */
/* Redemptions                                                                */
/* -------------------------------------------------------------------------- */

export type RedemptionFilter = PrizeRedemptionStatus | 'ALL';

/** Staff open this screen to hand prizes over, so the queue comes first. */
export const DEFAULT_REDEMPTION_FILTER: RedemptionFilter = 'PENDING';

export function redemptionsPath(filter: RedemptionFilter = DEFAULT_REDEMPTION_FILTER): string {
  return filter === 'ALL' ? REDEMPTIONS_PATH : `${REDEMPTIONS_PATH}?status=${filter}`;
}

export function fulfilRedemption(id: string): Promise<PrizeRedemptionDto> {
  return api.post<PrizeRedemptionDto>(`${REDEMPTIONS_PATH}/${id}/fulfil`);
}

export type CancelOutcome = { ok: true } | { ok: false; message: string };

/**
 * Validates the reason with the SAME zod schema the server applies, and only
 * touches the network once it passes. A refused reason therefore never turns
 * into a round-trip, and the message shown is the contract's own wording.
 *
 * API failures (e.g. the `not_pending` race) still throw — the caller decides
 * whether that is a dialog error or a "refresh the list" moment.
 */
export async function submitCancellation(id: string, reason: string): Promise<CancelOutcome> {
  const parsed = cancelRedemptionSchema.safeParse({ reason });
  if (!parsed.success) {
    return {
      ok: false,
      message: parsed.error.issues[0]?.message ?? 'Say why this redemption is being cancelled.',
    };
  }
  await api.post<PrizeRedemptionDto>(`${REDEMPTIONS_PATH}/${id}/cancel`, parsed.data);
  return { ok: true };
}

/* -------------------------------------------------------------------------- */
/* Challenges                                                                 */
/* -------------------------------------------------------------------------- */

export function metricKey(metric: ChallengeMetric): string {
  return `portalContent.metrics.${metric}`;
}

/** ISO datetime → the value a `datetime-local` input wants, in local time. */
export function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (part: number) => String(part).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

/**
 * `datetime-local` value → UTC ISO. The schema's `z.iso.datetime()` accepts
 * UTC, not zoneless local strings, so the browser's timezone is resolved here.
 */
export function localInputToIso(value: string): string | null {
  if (!value.trim()) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

/** Numeric text input → integer; blank stays absent so zod says what's wrong. */
function toInt(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
}

/** Drawer state: text inputs stay strings until submit so typing is unfussy. */
export interface ChallengeFormState {
  kind: ChallengeKind;
  title: string;
  description: string;
  imageMediaAssetId: string | null;
  pointsReward: string;
  targetValue: string;
  /**
   * '' is the UNCHOSEN state of the select, not a saveable value.
   *
   * It used to mean "one-shot", which was a fiction: nothing accrues against a
   * null metric, so such a challenge could never complete or pay. The API now
   * refuses it, and `challengeMissingMetric` catches it before the round trip.
   */
  metric: ChallengeMetric | '';
  /** datetime-local values; '' = open-ended. */
  startsAt: string;
  endsAt: string;
  isActive: boolean;
  sortOrder: string;
  /** '' = all games (venue-wide, `gameId: null`); otherwise the game's id. */
  gameId: string;
}

/** The drawer's initial state for `challenge`; null = the blank create form. */
export function challengeFormFromDto(challenge: ChallengeDto | null): ChallengeFormState {
  return {
    kind: challenge?.kind ?? 'CHALLENGE',
    title: challenge?.title ?? '',
    description: challenge?.description ?? '',
    imageMediaAssetId: challenge?.imageMediaAssetId ?? null,
    gameId: challenge?.gameId ?? '',
    pointsReward: challenge == null ? '' : String(challenge.pointsReward),
    targetValue: challenge?.targetValue == null ? '' : String(challenge.targetValue),
    metric: challenge?.metric ?? '',
    startsAt: isoToLocalInput(challenge?.startsAt ?? null),
    endsAt: isoToLocalInput(challenge?.endsAt ?? null),
    isActive: challenge?.isActive ?? true,
    sortOrder: String(challenge?.sortOrder ?? 0),
  };
}

export function toChallengePayload(form: ChallengeFormState): CreateChallengeInput {
  return {
    kind: form.kind,
    title: form.title,
    description: form.description.trim() || null,
    imageMediaAssetId: form.imageMediaAssetId,
    gameId: form.gameId === '' ? null : form.gameId,
    pointsReward: toInt(form.pointsReward) ?? 0,
    // Both are required by the contract; `challengeMissingMetric` and
    // `challengeMissingTarget` stop the drawer submitting without them, so the
    // fallbacks here only exist to satisfy the types on a blocked path.
    targetValue: toInt(form.targetValue) ?? 0,
    metric: (form.metric === '' ? 'hours_played' : form.metric) as ChallengeMetric,
    startsAt: localInputToIso(form.startsAt),
    endsAt: localInputToIso(form.endsAt),
    isActive: form.isActive,
    sortOrder: toInt(form.sortOrder) ?? 0,
  };
}

/**
 * PATCH body for an edit: only the fields whose value differs from what the
 * drawer loaded. Sending the full payload wrote fields the venue changed
 * underneath the open drawer — most damagingly a prize's `stock`, which
 * customers decrement by redeeming: saving a name typo restored the stale
 * count and staff handed out prizes they no longer had. The baseline is the
 * payload an UNTOUCHED form would produce (`challengeFormFromDto` is exactly
 * the drawer's initial state), so an untouched field can never appear.
 */
export function toChallengeUpdatePayload(
  form: ChallengeFormState,
  loaded: ChallengeDto,
): UpdateChallengeInput {
  return changedFields(toChallengePayload(form), toChallengePayload(challengeFormFromDto(loaded)));
}

/**
 * A challenge with no target can never complete for customers — the drawer
 * refuses to save that shape.
 */
export function challengeMissingTarget(form: ChallengeFormState): boolean {
  return toInt(form.targetValue) === null;
}

/**
 * And one with no metric has nothing to count, so it can never even start.
 *
 * The select used to offer a blank "one-shot" option; picking it produced a
 * challenge that sat in the customer's Arcade forever at 0%. Separate from the
 * target check so each field shows its own reason.
 */
export function challengeMissingMetric(form: ChallengeFormState): boolean {
  return form.metric === '';
}

/* -------------------------------------------------------------------------- */
/* Challenges by game                                                         */
/* -------------------------------------------------------------------------- */

/**
 * One row of `GET /engagement/challenge-games`. Mirrors the API's local
 * `ChallengeGameOption`; neither side needs it in the shared contract.
 */
export interface ChallengeGameOption {
  id: string;
  title: string;
  isActive: boolean;
}

export interface GamePickerOption {
  id: string;
  title: string;
  /** Known to be switched off in Settings > Games — labelled, not hidden. */
  inactive: boolean;
}

/**
 * What the drawer's Game picker lists: the venue's ACTIVE games, plus the one
 * this challenge is already about when that one is not among them — switched
 * off since, or the list not loaded yet (the DTO carries its title). Without
 * it the select would show "All games" for a challenge that is not, and an
 * unrelated save would look like it had moved it.
 */
export function gamePickerOptions(
  games: readonly ChallengeGameOption[] | null,
  current: Pick<ChallengeDto, 'gameId' | 'gameTitle'> | null,
): GamePickerOption[] {
  const options: GamePickerOption[] = (games ?? [])
    .filter((game) => game.isActive)
    .map((game) => ({ id: game.id, title: game.title, inactive: false }));

  if (current?.gameId && !options.some((option) => option.id === current.gameId)) {
    const listed = games?.find((game) => game.id === current.gameId);
    options.push({
      id: current.gameId,
      title: listed?.title ?? current.gameTitle ?? current.gameId,
      inactive: listed ? !listed.isActive : false,
    });
    options.sort((a, b) => a.title.localeCompare(b.title));
  }

  return options;
}

/**
 * The list's game filter: everything, only the venue-wide ones, or one game.
 * Game ids ride behind a prefix so no id can ever collide with a mode.
 */
export type ChallengeGameFilter = 'all' | 'venue' | `game:${string}`;

export const DEFAULT_CHALLENGE_GAME_FILTER: ChallengeGameFilter = 'all';

/**
 * The games the list can be filtered to — only those some challenge is
 * actually about, by title. Empty means no challenge names a game, and the
 * page shows no filter at all: a control that cannot change what is on
 * screen is decoration (the vault chips' rule).
 */
export function challengeGameFilters(
  challenges: readonly ChallengeDto[],
): { value: ChallengeGameFilter; title: string }[] {
  const seen = new Map<string, string>();
  for (const challenge of challenges) {
    if (challenge.gameId && !seen.has(challenge.gameId)) {
      seen.set(challenge.gameId, challenge.gameTitle ?? challenge.gameId);
    }
  }
  return [...seen]
    .map(([gameId, title]) => ({ value: `game:${gameId}` as const, title }))
    .sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * The filter actually in force. A game no row names any more (its last
 * challenge just moved) falls back to everything, and so does any filter
 * once the control itself is hidden — otherwise staff are left in front of
 * a short or empty list with no visible cause. The page renders the select
 * from this value too, so the two can never disagree.
 */
export function effectiveChallengeGameFilter(
  challenges: readonly ChallengeDto[],
  filter: ChallengeGameFilter,
): ChallengeGameFilter {
  if (filter === 'all') return filter;
  const filters = challengeGameFilters(challenges);
  if (filters.length === 0) return 'all';
  if (filter === 'venue') return filter;
  return filters.some((option) => option.value === filter) ? filter : 'all';
}

/**
 * The challenges a filter puts on screen. A game filter matches that game
 * alone — staff asking "what did we set for CS2" — not the venue-wide ones
 * the kiosk also shows on that game's page.
 */
export function filterChallengesByGame(
  challenges: readonly ChallengeDto[],
  filter: ChallengeGameFilter,
): readonly ChallengeDto[] {
  if (filter === 'all') return challenges;
  if (filter === 'venue') return challenges.filter((challenge) => challenge.gameId === null);

  const gameId = filter.slice('game:'.length);
  return challenges.filter((challenge) => challenge.gameId === gameId);
}

/** Keys of `next` whose value differs from `baseline` — the PATCH-worthy set. */
function changedFields<T extends object>(next: T, baseline: T): Partial<T> {
  const diff: Partial<T> = {};
  for (const key of Object.keys(next) as (keyof T)[]) {
    if (!Object.is(next[key], baseline[key])) diff[key] = next[key];
  }
  return diff;
}

/* -------------------------------------------------------------------------- */
/* Prizes                                                                     */
/* -------------------------------------------------------------------------- */

export interface PrizeFormState {
  name: string;
  description: string;
  imageMediaAssetId: string | null;
  /** '' = uncategorised. */
  prizeCategoryId: string;
  pointsCost: string;
  /** '' = UNLIMITED stock — maps to null, never 0 (0 means sold out). */
  stock: string;
  isActive: boolean;
  sortOrder: string;
}

/** The drawer's initial state for `prize`; null = the blank create form. */
export function prizeFormFromDto(prize: PrizeItemDto | null): PrizeFormState {
  return {
    name: prize?.name ?? '',
    description: prize?.description ?? '',
    imageMediaAssetId: prize?.imageMediaAssetId ?? null,
    prizeCategoryId: prize?.prizeCategoryId ?? '',
    pointsCost: prize == null ? '' : String(prize.pointsCost),
    // Null stock is UNLIMITED and renders blank; 0 (sold out) renders as '0'.
    stock: prize?.stock == null ? '' : String(prize.stock),
    isActive: prize?.isActive ?? true,
    sortOrder: String(prize?.sortOrder ?? 0),
  };
}

export function toPrizeItemPayload(form: PrizeFormState): CreatePrizeItemInput {
  return {
    name: form.name,
    description: form.description.trim() || null,
    imageMediaAssetId: form.imageMediaAssetId,
    prizeCategoryId: form.prizeCategoryId === '' ? null : form.prizeCategoryId,
    pointsCost: toInt(form.pointsCost) ?? 0,
    // Blank means "never runs out". toInt maps '' to null; '0' stays 0.
    stock: toInt(form.stock),
    isActive: form.isActive,
    sortOrder: toInt(form.sortOrder) ?? 0,
  };
}

/**
 * Same diff-before-PATCH as `toChallengeUpdatePayload`, and `stock` is the
 * reason the mechanism exists: it decrements underneath the open drawer with
 * every customer redemption, so it must only cross the wire when staff
 * actually retyped it. Blank still means unlimited: clearing a numeric stock
 * diffs to `stock: null` and is sent.
 */
export function toPrizeItemUpdatePayload(
  form: PrizeFormState,
  loaded: PrizeItemDto,
): UpdatePrizeItemInput {
  return changedFields(toPrizeItemPayload(form), toPrizeItemPayload(prizeFormFromDto(loaded)));
}
